<template>
    <div class="panel">
        <h2 class="uppercase text-blue-light font-thin text-lg mb-4">
            {{ $t('permitting_types_and_groups') }}
        </h2>

        <b-table 
            :data="permissionTypes"
            :loading="isLoading"
            detailed detail-key="id"
            :show-detail-icon="true"
            paginated striped bordered icon-pack="fas"
            :per-page="perPage">

            <b-table-column field="code" :label="$t('permitting_type')" v-slot="props">
                {{ props.row.name }}
            </b-table-column>

            <b-table-column :label="$t('groups')" width="120" centered v-slot="props">
                <span>{{ props.row.groups.length }}</span>
                <b-button type="is-text" size="is-small" class="has-text-primary"
                    @click.stop="openCreateGroupModal(props.row)">
                    <b-icon pack="fas" icon="plus-circle" />
                </b-button>
            </b-table-column>

            <template #detail="props">
                <div class="ml-6 mb-4">
                    <h3 class="font-bold mb-2">{{ $t('groups') }}</h3>
                    <div v-if="props.row.groups && props.row.groups.length">
                        <b-table :data="props.row.groups" detailed detail-key="id" :show-detail-icon="true"
                            :paginated="false" :striped="false" :bordered="false" icon-pack="fas">
                            <b-table-column field="name" :label="$t('name')" v-slot="grp">
                                {{ grp.row.name }}
                            </b-table-column>
                            <b-table-column :label="$t('permitting')" width="120" centered v-slot="grp">
                                <div class="flex items-center justify-center space-x-2">
                                     <span>{{ grp.row.templates.length }}</span>
                                    <b-tooltip :label="$t('add_permitting')" position="is-left">
                                    <div @click.stop="openCreateTemplateModal(props.row, grp.row)" class="cursor-pointer flex text-green ml-2">
                                        <b-icon pack="fas" icon="plus-circle" />
                                    </div>
                                    </b-tooltip>
                                    <b-tooltip :label="$t('edit')" position="is-left">
                                    <div @click.stop="editGroup(grp.row, props.row)" class="cursor-pointer flex text-blue ml-2">
                                        <b-icon pack="fas" icon="pencil-alt" />
                                    </div>
                                    </b-tooltip>
                                    <b-tooltip :label="$t('remove')" position="is-left" type="is-danger">
                                    <div @click.stop="deleteGroup(grp.row)" class="cursor-pointer flex text-red ml-2">
                                        <b-icon pack="fas" icon="trash-alt" />
                                    </div>
                                    </b-tooltip>
                                </div>
                            </b-table-column>
                            <template #detail="grp">
                                <div class="ml-6 mb-2">
                                    <h4 class="font-bold mb-1">{{ $t('permitting') }}</h4>
                                    <div v-if="grp.row.templates && grp.row.templates.length">
                                        <b-table :data="grp.row.templates" :paginated="false" :striped="true"
                                            :bordered="true" :hoverable="true" :narrowed="true"
                                            :pagination-simple="true" detail-key="id" icon-pack="fas">
                                            <b-table-column field="code" :label="$t('code')" v-slot="tpl">
                                                <span class="font-mono text-blue-700">{{ tpl.row.code }}</span>
                                            </b-table-column>
                                            <b-table-column field="name" :label="$t('name')" v-slot="tpl">
                                                {{ tpl.row.name }}
                                            </b-table-column>
                                            <b-table-column field="description" :label="$t('description')" v-slot="tpl">
                                                {{ tpl.row.description }}
                                            </b-table-column>
                                            <b-table-column :label="$t('actions')" width="60" centered v-slot="tpl">
                                                <div class="flex items-center justify-center space-x-2">
                                                    <b-tooltip :label="$t('edit')" position="is-left">
                                                        <div @click.stop="editTemplate(grp.row, tpl.row)" class="cursor-pointer flex text-blue ml-2">
                                                            <b-icon pack="fas" icon="pencil-alt" />
                                                        </div>
                                                    </b-tooltip>
                                                    <b-tooltip :label="$t('remove')" position="is-left" type="is-danger">
                                                        <div @click.stop="detachTemplateFromGroup(grp.row.id, tpl.row.id)" class="cursor-pointer flex text-red ml-2">
                                                            <b-icon pack="fas" icon="trash-alt" />
                                                        </div>
                                                    </b-tooltip>
                                                </div>
                                            </b-table-column>
                                        </b-table>
                                    </div>
                                    <div v-else class="text-center py-2">
                                        {{ $t('no_results') }}
                                    </div>
                                </div>
                            </template>
                        </b-table>
                    </div>
                    <div v-else class="text-center py-4">
                        {{ $t('no_results') }}
                    </div>
                </div>
            </template>
            <template #empty>
                <div class="has-text-centered py-6">{{ $t('no_results') }}</div>
            </template>
        </b-table>

        <!-- Modal: Crear Grupo -->
        <b-modal :active.sync="isCreateGroupModalActive" :width="500" has-modal-card>
            <div class="modal-card">
                <header class="modal-card-head">
                    <p class="modal-card-title">{{ $t('add_group') }}</p>
                    <button class="delete" aria-label="close" @click="isCreateGroupModalActive = false"></button>
                </header>
                <section class="modal-card-body">
                    <b-field :label="$t('code')">
                        <b-input v-model="newGroup.code" :placeholder="$t('code')" required />
                    </b-field>
                    <b-field :label="$t('name')">
                        <b-input v-model="newGroup.name" :placeholder="$t('name')" required />
                    </b-field>
                    <b-field :label="$t('description')">
                        <b-input v-model="newGroup.description" :placeholder="$t('description')" />
                    </b-field>
                </section>
                <footer class="modal-card-foot">
                   <b-button type="is-primary" @click="saveGroup" :disabled="!isValidNewGroup">
                        {{ newGroup.id ? $t('update') : $t('add_generic') }}
                   </b-button>
                    <b-button @click="isCreateGroupModalActive = false">{{ $t('cancel') }}</b-button>
                </footer>
            </div>
        </b-modal>

        <!-- Modal: Crear Template en Grupo -->
        <b-modal :active.sync="isCreateTemplateModalActive" :width="500" has-modal-card>
            <div class="modal-card">
                <header class="modal-card-head">
                    <p class="modal-card-title">{{ $t('add_generic') + ' ' + $t('permitting') }}</p>
                    <button class="delete" aria-label="close" @click="isCreateTemplateModalActive = false"></button>
                </header>
                <section class="modal-card-body">
                    <b-field :label="$t('code')">
                        <b-input v-model="newTemplate.code" :placeholder="$t('code')" required />
                    </b-field>
                    <b-field :label="$t('name')">
                        <b-input v-model="newTemplate.name" :placeholder="$t('name')" required />
                    </b-field>
                    <b-field :label="$t('description')">
                        <b-input v-model="newTemplate.description" :placeholder="$t('description')" />
                    </b-field>
                </section>
                <footer class="modal-card-foot">
                   <b-button type="is-primary" 
                        @click="newTemplate.id ? updateTemplateForGroup() : createTemplateForGroup()" 
                        :disabled="!isValidNewTemplate">
                        {{ newTemplate.id ? $t('update') : $t('add_generic') }}
                    </b-button>
                    <b-button @click="isCreateTemplateModalActive = false">{{ $t('cancelar') }}</b-button>
                </footer>
            </div>
        </b-modal>
    </div>
</template>

<script>
import axios from 'axios'
export default {
    props: {
        company: { type: Object, required: true }
    },

    data() {
        return {
            permissionTypes: [],
            isLoading: false,
            perPage: 5,
            isCreateGroupModalActive: false,
            newGroup: { name: '', code: '', description: '' },
            isCreateTemplateModalActive: false,
            newTemplate: { name: '', code: '', description: '' },
            currentType: null,
            currentGroup: null,
        }
    },

    mounted() {
        this.fetchPermissionTypes()
    },

    computed: {
        isValidNewGroup() {
            return this.newGroup.name && this.newGroup.code;
        },
        isValidNewTemplate() {
            return this.newTemplate.name && this.newTemplate.code;
        }
    },

    methods: {

        async fetchPermissionTypes() {
            this.isLoading = true
            try {
                const { data } = await axios.get(`/api/v2/company/${this.company.id}/permitting-types-with-groups`)
                this.permissionTypes = data.types
            } catch {
                this.$buefy.toast.open({ message: this.$t('task_insert_error'), type: 'is-danger' })
            } finally {
                this.isLoading = false
            }
        },

        openCreateGroupModal(type) {
            this.currentType = type
            this.isCreateGroupModalActive = true
            this.newGroup = { name: '', code: '', description: '' }
        },

        async saveGroup() {
            try {
                if (this.newGroup.id) {
                await axios.put(`/api/v2/permitting-group/${this.newGroup.id}`, {
                    name: this.newGroup.name,
                    code: this.newGroup.code,
                    description: this.newGroup.description,
                    permitting_type_id: this.currentType.id
                });
                this.$buefy.toast.open({ message: this.$t('update_successful'), type: 'is-success' });
                } else {
                await axios.post('/api/v2/permitting-group', {
                    name: this.newGroup.name,
                    code: this.newGroup.code,
                    description: this.newGroup.description,
                    permitting_type_id: this.currentType.id
                });
                this.$buefy.toast.open({ message: this.$t('task_insert_success'), type: 'is-success' });
                }
                this.isCreateGroupModalActive = false;
                this.fetchPermissionTypes();
            } catch (error) {
                let msg =
                error.response?.data?.message ||
                error.response?.data?.msg ||
                (error.response?.data?.errors
                    ? Object.values(error.response.data.errors).flat().join(', ')
                    : this.$t('task_insert_error'));
                this.$buefy.toast.open({ message: msg, type: 'is-danger' });
            }
        },

        openCreateTemplateModal(type, group) {
            this.currentType = type
            this.currentGroup = group
            this.isCreateTemplateModalActive = true
            this.newTemplate = { name: '', code: '', description: '' }
        },

        async createTemplateForGroup() {
            try {
                await axios.post('/api/v2/permitting-template', {
                    name: this.newTemplate.name,
                    code: this.newTemplate.code,
                    description: this.newTemplate.description,
                    company_id: this.company.id,
                    group_id: this.currentGroup.id
                })
                this.$buefy.toast.open({ message: this.$t('task_insert_success'), type: 'is-success' })
                this.isCreateTemplateModalActive = false
                this.fetchPermissionTypes()
            } catch (error) {
                let msg = error.response?.data?.message || error.response?.data?.msg || error.response?.data?.errors
                    ? Object.values(error.response.data.errors).flat().join(', ')
                    : this.$t('task_insert_error')
                this.$buefy.toast.open({ message: msg, type: 'is-danger' })
            }
        },

        editGroup(group, type) {
            this.currentType = type;
            this.newGroup = { ...group }; 
            this.isCreateGroupModalActive = true;
        },

        editTemplate(group, template) {
            this.currentGroup = group;
            this.currentType = this.permissionTypes.find(t => t.groups.some(g => g.id === group.id));
            this.newTemplate = { ...template };
            this.isCreateTemplateModalActive = true;
        },

        async deleteGroup(group) {
            try {
                this.$dialog.confirm({
                title: this.$t('remove'),
                message: this.$t('delete_confirm_text', [group.name]),
                confirmText: this.$t('remove'),
                type: 'is-danger',
                hasIcon: true,
                onConfirm: async () => {
                    await axios.delete(`/api/v2/permitting-group/${group.id}`);
                    this.$buefy.toast.open({ message: this.$t('removed_successfully'), type: 'is-success' });
                    this.fetchPermissionTypes();
                }
                });
            } catch (error) {
                this.$buefy.toast.open({
                message: this.$t('remove_error') + (error.response?.data?.message || ''),
                type: 'is-danger'
                });
            }
        },

        async detachTemplateFromGroup(groupId, templateId) {
            try {
                this.$dialog.confirm({
                title: this.$t('remove'),
                message: this.$t('delete_confirm_text', ['el permiso']),
                confirmText: this.$t('remove'),
                type: 'is-danger',
                hasIcon: true,
                onConfirm: async () => {
                    await axios.delete(`/api/v2/permitting-group/${groupId}/template/${templateId}`);
                    this.$buefy.toast.open({ message: this.$t('removed_successfully'), type: 'is-success' });
                    this.fetchPermissionTypes();
                }
                });
            } catch (error) {
                this.$buefy.toast.open({
                message: this.$t('remove_error') + (error.response?.data?.message || ''),
                type: 'is-danger'
                });
            }
        },

        async updateTemplateForGroup() {
            try {
                await axios.put(`/api/v2/permitting-template/${this.newTemplate.id}`, {
                    name: this.newTemplate.name,
                    code: this.newTemplate.code,
                    description: this.newTemplate.description,
                    company_id: this.company.id,
                    group_id: this.currentGroup.id
                });
                this.$buefy.toast.open({ message: this.$t('update_successful'), type: 'is-success' });
                this.isCreateTemplateModalActive = false;
                this.fetchPermissionTypes();
            } catch (error) {
                let msg = error.response?.data?.message || error.response?.data?.msg || error.response?.data?.errors
                    ? Object.values(error.response.data.errors).flat().join(', ')
                    : this.$t('task_insert_error');
                this.$buefy.toast.open({ message: msg, type: 'is-danger' });
            }
        },
    },

}
</script>